import Table from "@components/vendor/dashboard/table";
import { TTableColumns } from "@components/vendor/dashboard/table/types";
import { ActionIcon } from "@mantine/core";
import RoutesInstance from "@routes/instances";
import { useMutation } from "@tanstack/react-query";
import Common from "@utilities/common";
import DateHelper from "@utilities/date";
import { FC } from "react";
import { BiEdit } from "react-icons/bi";
import { IoMdRemoveCircle } from "react-icons/io";
import {
    Link,
    useLocation,
    useNavigate,
    useSearchParams,
} from "react-router-dom";
import Filters from "./filters";
import { VscFolderActive } from "react-icons/vsc";
import { useAuth } from "@contexts/auth";

interface Props {
    data: Array<ITags["get"]>;
    pagination: Required<IReturnValue["pagination"]>;
    loading: boolean;
}

const TagsContainer: FC<Props> = ({ data, loading, pagination }) => {
    const { pathname } = useLocation();

    const [URLSearchParams] = useSearchParams();

    const Navigate = useNavigate();

    const { profile } = useAuth();

    const { mutate, isLoading } = useMutation(
        ["removeTag"],
        (id: number) =>
            Common.api.tags.Delete({
                id: id,
            }),
        {
            onSuccess: () => {
                URLSearchParams.set("refetch", "true");

                Navigate(
                    {
                        pathname,
                        search: "?" + URLSearchParams.toString(),
                    },
                    {
                        replace: true,
                    },
                );
            },
        },
    );

    const { mutate: setTagMain, isLoading: isSetTagMainLoading } = useMutation(
        ["setTagMain"],
        (id: number) =>
            Common.api.tags.SetMain({
                id: id,
            }),
        {
            onSuccess: () => {
                URLSearchParams.set("refetch", "true");

                Navigate(
                    {
                        pathname,
                        search: "?" + URLSearchParams.toString(),
                    },
                    {
                        replace: true,
                    },
                );
            },
        },
    );

    const handleRemove = (id: number) => {
        mutate(id);
    };

    const handleSetMain = (id: number) => {
        setTagMain(id);
    };

    const columns: TTableColumns<ITags["get"]> = [
        {
            id: "image",
            accessorKey: "تصویر",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {data.row.original.image ? (
                        <img
                            width={48}
                            height={48}
                            src={data.row.original.image}
                            alt={data.row.original.faName}
                        />
                    ) : (
                        <div className="w-12">-</div>
                    )}
                </div>
            ),
        },
        {
            id: "faName",
            accessorKey: "نام فارسی",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {data.row.original.faName}
                </div>
            ),
        },
        {
            id: "enName",
            accessorKey: "نام انگلیسی",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {data.row.original.enName}
                </div>
            ),
        },
        {
            id: "seoName",
            accessorKey: "نام سئو",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {data.row.original.seoName}
                </div>
            ),
        },
        {
            id: "createDateTime",
            accessorKey: "زمان ساخت",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {DateHelper({
                        datetime: data.row.original.createDateTime,
                        format: "DD MMMM YYYY - HH:mm",
                    })}
                </div>
            ),
        },
        {
            accessorKey: "عملیات",
            cell: (data) => (
                <div className="flex items-center flex-wrap">
                    {profile.role !== "admin" && (
                        <ActionIcon
                            variant="subtle"
                            color="blue"
                            radius={9999}
                            className="mx-1"
                            size="lg"
                            loading={isSetTagMainLoading}
                            disabled={
                                isLoading ||
                                isSetTagMainLoading ||
                                data.row.original.isMain
                            }
                            onClick={() => handleSetMain(data.row.original.id)}>
                            <VscFolderActive className="text-2xl" />
                        </ActionIcon>
                    )}

                    <Link
                        to={RoutesInstance.editTag.replace(
                            ":id",
                            data.row.original.id.toString(),
                        )}>
                        <ActionIcon
                            variant="subtle"
                            color="yellow"
                            radius={9999}
                            className="mx-1"
                            size="lg"
                            disabled={isLoading || isSetTagMainLoading}>
                            <BiEdit className="text-2xl" />
                        </ActionIcon>
                    </Link>

                    {profile.role !== "admin" && (
                        <ActionIcon
                            variant="subtle"
                            color="red"
                            radius={9999}
                            className="mx-1"
                            size="lg"
                            loading={isLoading}
                            disabled={isLoading || isSetTagMainLoading}
                            onClick={() => handleRemove(data.row.original.id)}>
                            <IoMdRemoveCircle className="text-2xl" />
                        </ActionIcon>
                    )}
                </div>
            ),
        },
    ];

    return (
        <>
            <Filters />

            <Table<ITags["get"]>
                columns={columns}
                data={data}
                loading={loading}
                options={{
                    rowNumber: true,
                }}
                pagination={{
                    activePage: pagination?.page || 0,
                    isStartFromZero: false,
                    lastPage: pagination
                        ? Math.ceil(pagination?.total / pagination?.limit)
                        : 1,
                    queryKey: "page",
                }}
            />
        </>
    );
};

export default TagsContainer;
